// Tempo detection via autocorrelation on an energy envelope.
//
// Why not spectral / beat-tracking: those want FFT or HMM machinery.
// For loop-shaped material (a drum loop, a bassline, a vocal chop), an
// energy envelope already pulses at the beat rate, and autocorrelation
// over a plausible BPM window picks the period out cleanly. Same
// "no-FFT, just Float32 math" stance as onsetDetection.ts.
//
// Steps:
//   1. Hop-windowed RMS → coarse envelope (one value per hop).
//   2. Mean-subtract so the autocorrelation isn't dominated by DC.
//   3. Autocorrelate over lag values that map to MIN_BPM..MAX_BPM.
//   4. Peak → BPM, peak height vs. mean → confidence in [0, 1].
//
// Returns null when the input is too short or no clear peak emerges
// (caller decides whether to surface "unknown" or fall back).
//
// Octave caveat: autocorrelation cleanly identifies the dominant pulse
// period, but if a loop has strong eighth-note energy on top of a quarter-
// note pulse, the dominant period IS the eighth note — i.e. detectTempo
// will return 2× what a human would tap. There is no clean DSP fix for
// this without context the algorithm doesn't have ("which beat is the
// downbeat?"). Callers should give users a ÷2 / ×2 toggle and treat the
// detected value as a hint, not the truth.

const MIN_BPM = 60;
const MAX_BPM = 200;
// Hand-tuned: maps "peak is N standard deviations above the correlation
// mean" → confidence in [0, 1]. At N = CONFIDENCE_SIGMA_FULL_SCALE
// confidence saturates at 1.0. Smaller value = more generous; larger =
// more conservative. Tuned so a clean click track lands ≥ 0.6 and
// seeded white noise lands < 0.5 (see __tests__/tempoDetect.test.ts).
const CONFIDENCE_SIGMA_FULL_SCALE = 5;

export type TempoDetectOptions = {
  /** Override bpm search bounds. */
  minBpm?: number;
  maxBpm?: number;
  /** Hop size for the envelope, in samples. Default 512. */
  hopSize?: number;
  /** RMS window size, in samples. Default 1024. */
  windowSize?: number;
};

export type TempoEstimate = {
  bpm: number;
  /** Peak-vs-mean ratio normalised to roughly [0, 1]. Above ~0.4 is
   *  reliable; below means the autocorrelation was flat. */
  confidence: number;
};

export function detectTempo(
  channel: Float32Array,
  sampleRate: number,
  opts: TempoDetectOptions = {},
): TempoEstimate | null {
  const minBpm = opts.minBpm ?? MIN_BPM;
  const maxBpm = opts.maxBpm ?? MAX_BPM;
  const hopSize = opts.hopSize ?? 512;
  const windowSize = opts.windowSize ?? 1024;

  if (channel.length < windowSize * 2) return null;
  const envelope = rmsEnvelope(channel, windowSize, hopSize);
  if (envelope.length < 8) return null;

  // Mean-subtract so autocorr peaks aren't swallowed by DC.
  let mean = 0;
  for (let i = 0; i < envelope.length; i++) mean += envelope[i]!;
  mean /= envelope.length;
  const centered = new Float32Array(envelope.length);
  for (let i = 0; i < envelope.length; i++) centered[i] = envelope[i]! - mean;

  // BPM ↔ envelope-lag conversion. One envelope step is hopSize samples.
  const stepSeconds = hopSize / sampleRate;
  const minLag = Math.max(1, Math.floor(60 / (maxBpm * stepSeconds)));
  const maxLag = Math.min(
    centered.length - 1,
    Math.ceil(60 / (minBpm * stepSeconds)),
  );
  if (minLag >= maxLag) return null;

  // Autocorrelate over the search range only — that's the cheap part.
  const corrs = new Float32Array(maxLag - minLag + 1);
  for (let lag = minLag; lag <= maxLag; lag++) {
    let s = 0;
    const upto = centered.length - lag;
    for (let i = 0; i < upto; i++) s += centered[i]! * centered[i + lag]!;
    corrs[lag - minLag] = s / Math.max(1, upto);
  }

  // Best lag = argmax. Then refine via parabolic interpolation against
  // neighbours so the BPM has sub-step resolution.
  let bestIdx = 0;
  let bestVal = -Infinity;
  for (let i = 0; i < corrs.length; i++) {
    if (corrs[i]! > bestVal) {
      bestVal = corrs[i]!;
      bestIdx = i;
    }
  }
  const refinedIdx = parabolicPeak(corrs, bestIdx);
  const lag = minLag + refinedIdx;
  if (lag <= 0) return null;

  const bpm = 60 / (lag * stepSeconds);

  // Confidence: how many standard deviations above the mean is the peak.
  // A clear pulse has one tall peak in a sea of small correlations →
  // many sigmas above the mean. Noise has many similar-height peaks →
  // few sigmas. Mean-as-denominator (used initially) collapses to zero
  // for noise, falsely inflating ratios — std-dev is the honest scale.
  let corrMean = 0;
  for (let i = 0; i < corrs.length; i++) corrMean += corrs[i]!;
  corrMean /= corrs.length;
  let corrVar = 0;
  for (let i = 0; i < corrs.length; i++) {
    const d = corrs[i]! - corrMean;
    corrVar += d * d;
  }
  const corrStd = Math.sqrt(corrVar / corrs.length);
  const denom = corrStd > 1e-9 ? corrStd : 1e-9;
  const sigmasAboveMean = (bestVal - corrMean) / denom;
  const confidence = Math.max(
    0,
    Math.min(1, sigmasAboveMean / CONFIDENCE_SIGMA_FULL_SCALE),
  );

  return { bpm, confidence };
}

function rmsEnvelope(
  channel: Float32Array,
  windowSize: number,
  hopSize: number,
): Float32Array {
  const hopCount = Math.floor((channel.length - windowSize) / hopSize) + 1;
  const out = new Float32Array(Math.max(0, hopCount));
  for (let h = 0; h < hopCount; h++) {
    const start = h * hopSize;
    let sum = 0;
    for (let i = 0; i < windowSize; i++) {
      const v = channel[start + i] ?? 0;
      sum += v * v;
    }
    out[h] = Math.sqrt(sum / windowSize);
  }
  return out;
}

// Quadratic interpolation around a discrete peak. Returns a fractional
// index. Falls back to the integer index at boundaries.
function parabolicPeak(values: Float32Array, idx: number): number {
  if (idx <= 0 || idx >= values.length - 1) return idx;
  const yPrev = values[idx - 1]!;
  const yCur = values[idx]!;
  const yNext = values[idx + 1]!;
  const denom = yPrev - 2 * yCur + yNext;
  if (Math.abs(denom) < 1e-12) return idx;
  const offset = (0.5 * (yPrev - yNext)) / denom;
  if (offset < -1 || offset > 1) return idx;
  return idx + offset;
}
